// The park is a heightfield composed of primitives. The rideable height at any
// (x, z) is the max over every primitive covering that point, and the gradient
// comes from whichever primitive won. That keeps the skate physics arcade-simple:
// "the ground" is always one function, and ramps are just shapes in it.
//
// Coordinates: x east, z south, y up (three.js convention). Each primitive has a
// local frame rotated by `rot` about y: local u = (cos rot, sin rot) in (x, z),
// local w = (-sin rot, cos rot). Ramps rise toward +u.

import { ROADSIDE } from "./hill-dressing";
import type { HillRoad } from "./hill-road";
import type { Gap } from "./gaps";
import type { Tag } from "./graffiti";
import type { MapProp } from "./props";
import type { Target } from "./targets";
import { primAcross, primKind, type Prim } from "./prims";
import { BOWL, type BowlPrim } from "./prims/bowl";
import { BOX, type BoxPrim } from "./prims/box";
import { KICKER, type KickerPrim } from "./prims/kicker";
import { QUARTER, type QuarterPrim } from "./prims/quarter";
import { HUBBA, type HubbaPrim } from "./prims/hubba";
import { POOL, type PoolPrim } from "./prims/pool";
import { CHUTE, type ChutePrim } from "./prims/chute";
import { STAIRS, type StairsPrim } from "./prims/stairs";
import type { Scenery } from "./prims/scenery";
import type { Local } from "./prims/types";
import type { Vehicle } from "./vehicles";

export type { Prim } from "./prims";
export { bowlCoping } from "./prims/bowl";
export { boxLedges } from "./prims/box";
export { halfpipe, type HalfpipeSpec } from "./prims/halfpipe";
export { coping } from "./prims/quarter";
export { spine, type SpineSpec } from "./prims/spine";

export interface Vec2 {
  x: number;
  z: number;
}

export interface Rail {
  a: Vec2;
  b: Vec2;
  /** Height at `a`, and all along a level rail. */
  y: number;
  /** Height at `b` for a sloped rail (a stair set's handrail, #10); level at `y` when left out. */
  yb?: number;
  kind: "rail" | "ledge" | "coping";
  /**
   * One of a run of rails laid end to end round a curve (a chute's wall
   * tops, #187): a grind reaching one end carries on onto the joined rail
   * that starts there instead of popping off.
   */
  joined?: true;
  /**
   * A ride's track (#189, the coaster): grindable, drawn by its scenery
   * rather than as a rail, and the one thing that may run outside the fence.
   * Coming off it out there is a bail (`sim/ride-out.ts`).
   */
  track?: true;
  /** A chain lift (#189): grinding up this rail never drops below this speed (m/s), and it holds your balance. */
  lift?: number;
}

/** A rail's height at `t` (0 at `a`, 1 at `b`). */
export function railY(rail: Rail, t: number): number {
  return rail.yb === undefined ? rail.y : rail.y + (rail.yb - rail.y) * t;
}

export interface Level {
  /**
   * Half-size of the square park; the fence sits at ±bounds. A rectangular
   * park (`extent`) is never wider than this either way.
   */
  bounds: number;
  /**
   * A rectangular park (#115): the fence's half-size along x (east-west)
   * and z (north-south). Left out, the park is square, ±bounds both ways.
   * Ask `halfX` / `halfZ` (or `sideLimit` / `playable`), not `bounds`.
   */
  extent?: { x: number; z: number };
  prims: Prim[];
  rails: Rail[];
  spawn: { x: number; z: number; yaw: number };
  /** Named gaps (see gaps.ts); none when left out. */
  gaps?: Gap[];
  /** Sides that are a beach instead of a wall (#82); every side is a wall when left out. */
  edges?: SoftEdge[];
  /** Vehicles driving loops (#37), to skitch on; none when left out. */
  vehicles?: Vehicle[];
  /** Paintball targets (#137); none when left out. */
  targets?: Target[];
  /** Graffiti to wash off with the power washer (#151); none when left out. */
  graffiti?: Tag[];
  /** Light things for the power washer to knock about (#151): where each stands; none when left out. */
  props?: MapProp[];
  /** Rides, buildings and furniture (#156), drawn only: the sim never reads them. None when left out. */
  scenery?: Scenery[];
  /** A hill (#193, Bomb the Hill): the whole park tilts down toward +z, and you ride it in the hill mode (`sim/hill.ts`). */
  hill?: Hill;
}

/**
 * Bomb the Hill (#193): the floor falls `grade` metres per metre toward +z
 * (south), and everything on it falls with it, so a piece keeps its own
 * shape on the slope. `surfaceAt` adds the fall; the renderer shears the
 * park's meshes to match (`render/level-meshes.ts`).
 */
export interface Hill {
  grade: number;
  /**
   * What's in the road (#194, `hill-course.ts`), laid out from the map
   * file's `seed` and on forever (#195); none without one. Its ramps are
   * kickers in `prims` after the park's own (`parkPrims`), swapped as the
   * road moves (`sim/hill.ts`'s `placeRoad`), and its grind rails (#237)
   * are rails after the park's own (`parkRails`) the same way.
   */
  road?: HillRoad;
  /** The park's own primitives, without the road's ramps. */
  parkPrims: Prim[];
  /** The park's own rails, without the road's grind rails (#237). */
  parkRails: Rail[];
}

/** How far the floor at `z` sits below the floor at z = 0 on a hill (0 on a flat park). */
export function hillDrop(level: Level, z: number): number {
  return level.hill ? level.hill.grade * z : 0;
}

/** A side of the park: north is -z, south +z, east +x, west -x. */
export type EdgeSide = "north" | "south" | "east" | "west";

/**
 * A soft boundary (#82): no wall on this side. The last `sand` metres
 * inside the fence line are sand, which slows you to a stop and turns you
 * back; past the fence line is the sea if `water`, or more sand if not.
 */
export interface SoftEdge {
  side: EdgeSide;
  sand: number;
  water: boolean;
}

/** What the ground is made of at a spot. Concrete is everything that isn't a beach, and any piece standing on one. */
export type GroundKind = "concrete" | "sand" | "water";

/** How far past the fence line a soft side goes before it's out of bounds anyway (m). */
export const EDGE_FAR = 30;

/** How far the fence is from the middle along x (east and west sides). */
export function halfX(level: Level): number {
  return level.extent?.x ?? level.bounds;
}

/** How far the fence is from the middle along z (north and south sides). */
export function halfZ(level: Level): number {
  return level.extent?.z ?? level.bounds;
}

/** How far `side`'s fence line is from the middle. */
export function fenceAt(level: Level, side: EdgeSide): number {
  return side === "east" || side === "west" ? halfX(level) : halfZ(level);
}

/** How far out past `side`'s fence line (x, z) is: positive outside, negative inside. */
function pastFence(level: Level, side: EdgeSide, x: number, z: number): number {
  const c = side === "south" ? z : side === "north" ? -z : side === "east" ? x : -x;
  return c - fenceAt(level, side);
}

/** What the ground at (x, z) is made of: sand or sea on a soft side, concrete everywhere else. */
export function groundKind(level: Level, x: number, z: number): GroundKind {
  if (!level.edges) return "concrete";
  let kind: GroundKind = "concrete";
  for (const e of level.edges) {
    const past = pastFence(level, e.side, x, z);
    if (past > 0 && e.water) kind = "water";
    else if (past > -e.sand && kind !== "water") kind = "sand";
  }
  // A piece standing on the sand, or out over the sea (a pier, #243), is still a piece to skate and walk on.
  if (kind !== "concrete" && surfaceAt(level, x, z).h > 0) return "concrete";
  return kind;
}

/** Which way the concrete is from sand at (x, z): the inward normal of the soft sides it's on. */
export function towardConcrete(level: Level, x: number, z: number): { x: number; z: number } {
  let ix = 0;
  let iz = 0;
  for (const e of level.edges ?? []) {
    if (pastFence(level, e.side, x, z) <= -e.sand) continue;
    if (e.side === "south") iz -= 1;
    else if (e.side === "north") iz += 1;
    else if (e.side === "east") ix -= 1;
    else ix += 1;
  }
  const l = Math.hypot(ix, iz) || 1;
  return { x: ix / l, z: iz / l };
}

/**
 * How far from the middle toward `side` the park goes, `margin` short of its
 * edge: the fence on a wall side. On a soft side it's where the sand starts,
 * for anything that has to be on concrete (markers, hubs, the bot's lines,
 * `playcheck`'s grid); `open` goes out past the beach instead (the camera,
 * which follows you onto the sand).
 */
export function sideLimit(level: Level, side: EdgeSide, margin: number, reach: "concrete" | "open" = "concrete"): number {
  const edge = level.edges?.find((e) => e.side === side);
  const fence = fenceAt(level, side);
  if (!edge) return fence - margin;
  return (reach === "open" ? fence + EDGE_FAR : fence - edge.sand) - margin;
}

/** Whether (x, z) is on the park's concrete, `margin` inside its walls and off any sand (`sideLimit`). */
export function playable(level: Level, x: number, z: number, margin: number): boolean {
  if (!level.edges) return Math.abs(x) <= halfX(level) - margin && Math.abs(z) <= halfZ(level) - margin;
  return (
    x <= sideLimit(level, "east", margin) &&
    -x <= sideLimit(level, "west", margin) &&
    z <= sideLimit(level, "south", margin) &&
    -z <= sideLimit(level, "north", margin)
  );
}

/**
 * Whether (x, z) is past the park's edge: through a wall (0.4 m short of
 * the fence line, for the skater's size), or far out past a soft side.
 */
export function outOfBounds(level: Level, x: number, z: number): boolean {
  const bx = halfX(level) - 0.4;
  const bz = halfZ(level) - 0.4;
  // A hill's side street (#197) breaks the wall: you can ride off into it, up to the building corners either side.
  if (Math.abs(x) > bx && Math.abs(z) <= bz && level.hill?.road) return !inSideStreet(level, x, z);
  if (!level.edges) return Math.abs(x) > bx || Math.abs(z) > bz;
  const soft = (side: EdgeSide) => level.edges!.some((e) => e.side === side);
  const limit = (side: EdgeSide) => (soft(side) ? fenceAt(level, side) + EDGE_FAR : fenceAt(level, side) - 0.4);
  return x > limit("east") || -x > limit("west") || z > limit("south") || -z > limit("north");
}

/** Whether (x, z), off the side of a hill's road, is out in a side street (#197): between its building corners, short of its far end. */
function inSideStreet(level: Level, x: number, z: number): boolean {
  const road = level.hill?.road;
  if (!road) return false;
  const R = ROADSIDE.corner;
  for (const j of road.course.junctions) {
    // How far past the side street's edge (z) and the road's wall line (x).
    const az = Math.abs(z - j.z) - j.hl;
    const ax = Math.abs(x) - halfX(level);
    if (az <= -0.4) return ax <= j.street - 0.4;
    // Its corners are kerbed round (#211): in a corner, clear of the kerb's curve.
    if (az < R && ax < R && Math.hypot(R - ax, R - az) >= R + 0.4) return true;
  }
  return false;
}

export interface QuarterInfo {
  /** Horizontal unit vector pointing up the ramp (toward the coping). */
  axisX: number;
  axisZ: number;
  /** How far up the transition we are, 0 at the bottom, r at the coping. */
  u: number;
  r: number;
  /** Set where the wall bends round (a bowl's corner, #11): up the ramp turns as you go along it. */
  curved?: true;
}

export interface Surface {
  h: number;
  gx: number;
  gz: number;
  quarter?: QuarterInfo;
  /** Down a water slide's chute (#163): the way it runs, a unit direction the ride turns to follow. */
  guide?: { x: number; z: number };
}

/** Past this angle up a transition the skater launches straight up ("vert"). */
export const VERT_LAUNCH_ANGLE = (86 * Math.PI) / 180;

/**
 * A primitive compiled for `surfaceAt` (#120): one record shape for every
 * kind, with its rotation and a squared bounding radius worked out once.
 * Reading `p.rot`, `p.x`, `p.z` and calling `primKind(p).eval` at one site
 * over five primitive shapes made that site megamorphic, slowing every
 * map's ground lookups once any map with five kinds had run.
 */
interface CompiledPrim {
  kind: Prim["kind"];
  x: number;
  z: number;
  c: number;
  s: number;
  /** Squared distance from (x, z) past which the primitive can't cover a point. */
  reach2: number;
  prim: Prim;
}

/**
 * A uniform grid over the level (#165): each cell lists the things whose
 * reach touches it, by index and in index order, so a lookup that walks a
 * cell's list meets candidates in the same order a full scan would and
 * resolves ties exactly the same. Built once per level beside the
 * compiled primitives. Without it every lookup scanned every piece, so
 * the cost of a map's flow check went as its area times its piece count.
 */
interface Grid {
  x0: number;
  z0: number;
  nx: number;
  nz: number;
  cells: number[][];
}

/** A grid cell's size (m). */
const CELL = 8;
const EMPTY: readonly number[] = [];

/** A grid holding item `i` in every cell its box (`minX`..`maxX`, `minZ`..`maxZ`) touches. */
function buildGrid(boxes: readonly (readonly [minX: number, maxX: number, minZ: number, maxZ: number])[]): Grid {
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  for (const [a, b, c, d] of boxes) {
    x0 = Math.min(x0, a);
    x1 = Math.max(x1, b);
    z0 = Math.min(z0, c);
    z1 = Math.max(z1, d);
  }
  if (!boxes.length) return { x0: 0, z0: 0, nx: 0, nz: 0, cells: [] };
  const nx = Math.floor((x1 - x0) / CELL) + 1;
  const nz = Math.floor((z1 - z0) / CELL) + 1;
  const cells: number[][] = Array.from({ length: nx * nz }, () => []);
  boxes.forEach(([a, b, c, d], i) => {
    const ia = Math.floor((a - x0) / CELL);
    const ib = Math.floor((b - x0) / CELL);
    const ja = Math.floor((c - z0) / CELL);
    const jb = Math.floor((d - z0) / CELL);
    for (let ix = ia; ix <= ib; ix++) for (let jz = ja; jz <= jb; jz++) cells[jz * nx + ix]!.push(i);
  });
  return { x0, z0, nx, nz, cells };
}

/** The cell (x, z) is in, or -1 off the grid (where nothing reaches). */
function cellOf(g: Grid, x: number, z: number): number {
  const ix = Math.floor((x - g.x0) / CELL);
  const jz = Math.floor((z - g.z0) / CELL);
  // Written so NaN lands off the grid too.
  if (!(ix >= 0 && ix < g.nx && jz >= 0 && jz < g.nz)) return -1;
  return jz * g.nx + ix;
}

interface CompiledPrims {
  list: CompiledPrim[];
  grid: Grid;
}

/** Each level's primitives compiled once, keyed by its `prims` array. */
const COMPILED = new WeakMap<Prim[], CompiledPrims>();

function compilePrims(prims: Prim[]): CompiledPrims {
  const hit = COMPILED.get(prims);
  if (hit && hit.list.length === prims.length) return hit;
  const list = prims.map((p): CompiledPrim => {
    const [u0, u1] = primKind(p).extent(p);
    // A hair over the covered rectangle's far corner, so rounding in the
    // local transform can never reject a point `eval` would have covered.
    const [w0, w1] = primAcross(p);
    const reach = Math.hypot(Math.max(Math.abs(u0), Math.abs(u1)), Math.max(-w0, w1)) * (1 + 1e-9) + 1e-6;
    return { kind: p.kind, x: p.x, z: p.z, c: Math.cos(p.rot), s: Math.sin(p.rot), reach2: reach * reach, prim: p };
  });
  // Each primitive's square round its reach circle, a hair wider again.
  const grid = buildGrid(
    list.map((cp) => {
      const r = Math.sqrt(cp.reach2) * (1 + 1e-9) + 1e-6;
      return [cp.x - r, cp.x + r, cp.z - r, cp.z + r] as const;
    }),
  );
  const out = { list, grid };
  COMPILED.set(prims, out);
  return out;
}

/** One primitive's `eval`, through a switch so each kind's call site only ever sees that kind. */
function evalPrim(cp: CompiledPrim, u: number, w: number): Local | null {
  switch (cp.kind) {
    case "box":
      return BOX.eval(cp.prim as BoxPrim, u, w);
    case "kicker":
      return KICKER.eval(cp.prim as KickerPrim, u, w);
    case "quarter":
      return QUARTER.eval(cp.prim as QuarterPrim, u, w);
    case "bowl":
      return BOWL.eval(cp.prim as BowlPrim, u, w);
    case "stairs":
      return STAIRS.eval(cp.prim as StairsPrim, u, w);
    case "hubba":
      return HUBBA.eval(cp.prim as HubbaPrim, u, w);
    case "pool":
      return POOL.eval(cp.prim as PoolPrim, u, w);
    case "chute":
      return CHUTE.eval(cp.prim as ChutePrim, u, w);
  }
}

export function surfaceAt(level: Level, x: number, z: number): Surface {
  const s = parkSurfaceAt(level, x, z);
  if (!level.hill) return s;
  // On a hill (#193) the park tilts: the same surface, lower and steeper toward +z.
  s.h -= level.hill.grade * z;
  s.gz -= level.hill.grade;
  return s;
}

/** The park's surface at (x, z) as if the floor were flat. */
function parkSurfaceAt(level: Level, x: number, z: number): Surface {
  let best: Surface | null = null;
  let carved: Surface | null = null;
  let bestH = 0;
  const { list, grid } = compilePrims(level.prims);
  const cell = cellOf(grid, x, z);
  // Off the grid no primitive reaches; a cell's list is every primitive
  // whose reach touches it, in index order, so the rest would all be skipped.
  for (const i of cell < 0 ? EMPTY : grid.cells[cell]!) {
    const cp = list[i]!;
    const dx = x - cp.x;
    const dz = z - cp.z;
    if (dx * dx + dz * dz > cp.reach2) continue;
    const c = cp.c;
    const s = cp.s;
    const u = dx * c + dz * s;
    const w = -dx * s + dz * c;
    const l = evalPrim(cp, u, w);
    if (!l) continue;
    if (cp.kind === "pool") {
      // A pool (#163) is the floor where it covers: the first one there, kept aside.
      if (!carved) carved = surfaceOf(l, c, s);
      continue;
    }
    if (l.h < bestH) continue;
    // Ties go to the later primitive only if it is strictly higher, so a flat
    // deck beside a ramp does not steal the ramp's gradient at the seam.
    if (l.h === bestH && bestH !== 0) continue;
    bestH = l.h;
    best = surfaceOf(l, c, s);
  }
  // Where a pool is the floor, anything standing in it wins only where it's higher.
  if (carved && (!best || best.h < carved.h)) return carved;
  return best ?? { h: 0, gx: 0, gz: 0 };
}

/** A primitive's local result as a world `Surface`, turned by its (cos, sin). */
function surfaceOf(l: Local, c: number, s: number): Surface {
  const out: Surface = {
    h: l.h,
    gx: l.du * c - l.dw * s,
    gz: l.du * s + l.dw * c,
  };
  if (l.quarter) {
    const a = l.quarter.axis;
    const axisX = a ? a.u * c - a.w * s : c;
    const axisZ = a ? a.u * s + a.w * c : s;
    out.quarter = { axisX, axisZ, u: l.quarter.u, r: l.quarter.r };
    if (l.quarter.curved) out.quarter.curved = true;
  }
  if (l.guide) out.guide = { x: l.guide.u * c - l.guide.w * s, z: l.guide.u * s + l.guide.w * c };
  return out;
}

/**
 * The index into `level.prims` of the primitive `surfaceAt` takes the
 * ground at (x, z) from, or -1 on bare floor: the same search and the same
 * tie rule, through the same grid (#165).
 */
export function primIndexAt(level: Level, x: number, z: number): number {
  let best = -1;
  let bestH = 0;
  let pool = -1;
  let poolH = 0;
  const { list, grid } = compilePrims(level.prims);
  const cell = cellOf(grid, x, z);
  for (const i of cell < 0 ? EMPTY : grid.cells[cell]!) {
    const cp = list[i]!;
    const dx = x - cp.x;
    const dz = z - cp.z;
    if (dx * dx + dz * dz > cp.reach2) continue;
    const l = evalPrim(cp, dx * cp.c + dz * cp.s, -dx * cp.s + dz * cp.c);
    if (!l) continue;
    if (cp.kind === "pool") {
      if (pool < 0) {
        pool = i;
        poolH = l.h;
      }
      continue;
    }
    if (l.h < bestH || (l.h === bestH && bestH !== 0)) continue;
    best = i;
    bestH = l.h;
  }
  return pool >= 0 && (best < 0 || bestH < poolH) ? pool : best;
}

/**
 * Horizontal unit normal of the wall the skater ran into, pointing back out
 * toward open ground. `x, z` is the blocked position (just inside the
 * obstacle or past the fence); `fromX, fromZ` is where the skater was. The
 * heightfield has no explicit walls, so this samples a ring around the hit
 * and averages the directions where the ground drops away.
 */
export function wallNormal(
  level: Level,
  x: number,
  z: number,
  fromX: number,
  fromZ: number,
): { x: number; z: number } {
  const bx = halfX(level) - 0.4;
  const bz = halfZ(level) - 0.4;
  if (Math.abs(x) > bx && Math.abs(z) <= bz && Math.abs(fromX) > bx && level.hill?.road) {
    // Out in a hill's side street (#197), into a building corner: back toward the side street's middle.
    const R = ROADSIDE.corner;
    const j = level.hill.road.course.junctions.find((j) => Math.abs(fromZ - j.z) <= j.hl + R + 1);
    const ax = Math.abs(x) - halfX(level);
    const az = j ? Math.abs(z - j.z) - j.hl : 0;
    if (j && az > -0.4 && ax < R) {
      // Into a kerbed corner (#211): straight out from the kerb's curve.
      const nx = Math.sign(x) * (ax - R);
      const nz = (Math.sign(z - j.z) || 1) * (az - R);
      const l = Math.hypot(nx, nz) || 1;
      return { x: nx / l, z: nz / l };
    }
    if (j && Math.abs(x) <= halfX(level) + j.street) return { x: 0, z: Math.sign(j.z - z) || -1 };
  }
  if (Math.abs(x) > bx || Math.abs(z) > bz) {
    // The fence: push straight back in along whichever edge was crossed.
    const nx = x > bx ? -1 : x < -bx ? 1 : 0;
    const nz = z > bz ? -1 : z < -bz ? 1 : 0;
    const l = Math.hypot(nx, nz);
    return { x: nx / l, z: nz / l };
  }
  const top = surfaceAt(level, x, z).h;
  // Round a post thinner than the ring every sample drops away and they
  // cancel out (#124); smaller rings then find the nearest side.
  for (const r of WALL_RINGS) {
    let nx = 0;
    let nz = 0;
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * Math.PI * 2;
      const dx = Math.sin(a);
      const dz = Math.cos(a);
      if (surfaceAt(level, x + dx * r, z + dz * r).h < top - 0.2) {
        nx += dx;
        nz += dz;
      }
    }
    const l = Math.hypot(nx, nz);
    if (l >= 1e-6) return { x: nx / l, z: nz / l };
  }
  // Buried in something wide (or dead centre on a post): fall back to "back
  // where you came from", and never to no direction at all.
  const l = Math.hypot(fromX - x, fromZ - z);
  return l >= 1e-6 ? { x: (fromX - x) / l, z: (fromZ - z) / l } : { x: 0, z: -1 };
}

/** The sample rings `wallNormal` tries, widest first (m). */
const WALL_RINGS = [0.4, 0.2, 0.1, 0.05];

/** Closest point on a rail's horizontal segment, as a 0..1 parameter plus distance. */
export function nearestOnRail(rail: Rail, x: number, z: number): { t: number; dist: number } {
  const ax = rail.b.x - rail.a.x;
  const az = rail.b.z - rail.a.z;
  const len2 = ax * ax + az * az;
  let t = len2 === 0 ? 0 : ((x - rail.a.x) * ax + (z - rail.a.z) * az) / len2;
  t = Math.max(0, Math.min(1, t));
  const px = rail.a.x + ax * t;
  const pz = rail.a.z + az * t;
  return { t, dist: Math.hypot(x - px, z - pz) };
}

/** How close (m) the skater's centre gets to a rail's bar or posts before the board hits it. */
export const RAIL_RADIUS = 0.3;

/**
 * The farthest (m) a rail lookup through `railsNear` may reach out from a
 * point: the rail grid holds each rail in every cell within this of it.
 */
export const RAIL_NEAR = 1;

interface RailIndex {
  count: number;
  grid: Grid;
}

/** Each level's rail grid, keyed by its `rails` array. */
const RAIL_GRIDS = new WeakMap<Rail[], RailIndex>();

function railGrid(rails: Rail[]): RailIndex {
  const hit = RAIL_GRIDS.get(rails);
  if (hit && hit.count === rails.length) return hit;
  // A hair past RAIL_NEAR (and so past RAIL_RADIUS), for rounding.
  const pad = RAIL_NEAR * (1 + 1e-9) + 1e-6;
  const grid = buildGrid(
    rails.map(
      (r) =>
        [
          Math.min(r.a.x, r.b.x) - pad,
          Math.max(r.a.x, r.b.x) + pad,
          Math.min(r.a.z, r.b.z) - pad,
          Math.max(r.a.z, r.b.z) + pad,
        ] as const,
    ),
  );
  const out = { count: rails.length, grid };
  RAIL_GRIDS.set(rails, out);
  return out;
}

/**
 * The indices into `level.rails` of every rail that might come within
 * `RAIL_NEAR` of (x, z), in index order (#165): a search for rails that
 * close walks these instead of every rail and meets them in the same order.
 * Some may be farther; none nearer is left out.
 */
export function railsNear(level: Level, x: number, z: number): readonly number[] {
  const { grid } = railGrid(level.rails);
  const cell = cellOf(grid, x, z);
  return cell < 0 ? EMPTY : grid.cells[cell]!;
}
/** The board clears a rail once it's within this much of the bar's height (an ollie's nose is already up). */
const RAIL_CLEAR = 0.2;
/**
 * A ride's track this far or more over the board is overhead, not a fence
 * (#221): you skate under a coaster, between its supports, the way you
 * would under Sunny Beach v2's track out over the pier.
 */
export const TRACK_HEADROOM = 2.2;

function segmentsCross(ax: number, az: number, bx: number, bz: number, cx: number, cz: number, dx: number, dz: number): boolean {
  const side = (px: number, pz: number, qx: number, qz: number, rx: number, rz: number) =>
    (qx - px) * (rz - pz) - (qz - pz) * (rx - px);
  return (
    side(ax, az, bx, bz, cx, cz) * side(ax, az, bx, bz, dx, dz) < 0 &&
    side(cx, cz, dx, dz, ax, az) * side(cx, cz, dx, dz, bx, bz) < 0
  );
}

/**
 * The rail (bar or posts) that a move from `from` to `to` at height `y` runs
 * into, with the horizontal unit normal pointing back toward where you came
 * from. Only freestanding rails collide: ledges and coping already have a box
 * or ramp under them in the heightfield. Anything below the bar hits it (bar
 * and posts act as one fence), anything at or above it clears; on a sloped
 * rail that's the bar's height where you meet it. A ride's track
 * `TRACK_HEADROOM` or more overhead has no posts there: you pass under it. Only entering
 * the rail's radius from outside counts, so dropping onto a rail from above
 * (off a grind, say) lets you roll away instead of getting stuck in it.
 */
export function railHit(
  level: Level,
  fromX: number,
  fromZ: number,
  toX: number,
  toZ: number,
  y: number,
): { rail: Rail; n: { x: number; z: number } } | null {
  // A hit means the rail comes within RAIL_RADIUS of `to` or crosses the
  // move, so with both ends in one cell (which is convex) only that cell's
  // rails can be hit; a move across cells checks them all.
  const { grid } = railGrid(level.rails);
  const cell = cellOf(grid, fromX, fromZ);
  const all = cell < 0 || cell !== cellOf(grid, toX, toZ);
  const rails = level.rails;
  const near = all ? null : grid.cells[cell]!;
  const n = near ? near.length : rails.length;
  for (let k = 0; k < n; k++) {
    const rail = rails[near ? near[k]! : k]!;
    if (rail.kind !== "rail" || y >= Math.max(rail.y, rail.yb ?? rail.y) - RAIL_CLEAR) continue;
    const before = nearestOnRail(rail, fromX, fromZ);
    if (before.dist < RAIL_RADIUS) continue;
    const after = nearestOnRail(rail, toX, toZ);
    const bar = Math.max(railY(rail, before.t), railY(rail, after.t));
    if (y >= bar - RAIL_CLEAR || (rail.track && bar - y >= TRACK_HEADROOM)) continue;
    if (after.dist >= RAIL_RADIUS && !segmentsCross(fromX, fromZ, toX, toZ, rail.a.x, rail.a.z, rail.b.x, rail.b.z))
      continue;
    const px = rail.a.x + (rail.b.x - rail.a.x) * before.t;
    const pz = rail.a.z + (rail.b.z - rail.a.z) * before.t;
    return { rail, n: { x: (fromX - px) / before.dist, z: (fromZ - pz) / before.dist } };
  }
  return null;
}
